<form
  [formGroup]="form"
  class="form-fields-group"
  (keydown.enter)="handleEnter($event)"
>
  <div class="grid grid-cols-1 md:grid-cols-12 gap-3">
    @for (card of schema; track card) {
      <div class="sections" [ngClass]="card.className || 'col-span-12'">
        <app-form-card>
          @if (card.title || card.description || card.icon) {
            <app-form-card-header
              [title]="card.title"
              [description]="card.description"
              [icon]="card.icon"
            ></app-form-card-header>
          }

          <app-form-card-body>
            <div class="grid grid-cols-12 gap-3">
              @for (f of card.fields; track f) {
                @if (isFieldVisible(f)) {
                  <div
                    [ngClass]="f.className || 'col-span-12'"
                    class="form-field"
                  >
                    <ng-container
                      *ngTemplateOutlet="fieldTpl; context: { f: f }"
                    ></ng-container>
                  </div>
                }
              }
            </div>
          </app-form-card-body>
        </app-form-card>
      </div>
    }
  </div>
</form>

<!-- ══════════════════════════════════════════════════════════════
     SHARED FIELD TEMPLATE
══════════════════════════════════════════════════════════════ -->
<ng-template #fieldTpl let-f="f">
  <ng-container [formGroup]="form">
    <div class="flex flex-col gap-2 w-full relative">
      @switch (f.type) {
        @case ("TEXT") {
          <label [for]="f.key" class="form-label"
            >{{ f.label }}
            @if (f?.validators?.required) {
              <span class="text-red-500">*</span>
            }
            @if (f.calculated) {
              <span class="badge-calc">Calculated</span>
            }
          </label>
          <input
            type="text"
            autocomplete="off"
            class="form-control"
            [attr.placeholder]="f.placeholder"
            [readonly]="f.readonly || f.calculated"
            [formControlName]="f.key"
            [id]="f.key"
          />
        }

        @case ("PASSWORD") {
          <label [for]="f.key" class="form-label">{{ f.label }}</label>
          <div class="relative">
            <input
              [type]="showPasswordMap[f.key] ? 'text' : 'password'"
              autocomplete="off"
              class="form-control pr-10"
              [attr.placeholder]="f.placeholder"
              [readonly]="f.readonly || f.calculated"
              [formControlName]="f.key"
              [id]="f.key"
            />
            <span class="password-toggle" (click)="togglePassword(f.key)">
              <i
                class="fa"
                [ngClass]="showPasswordMap[f.key] ? 'fa-eye-slash' : 'fa-eye'"
              ></i>
            </span>
          </div>
        }

        @case ("TOGGLE") {
          <div class="flex flex-row items-center gap-2">
            <div class="form-check form-switch mb-0">
              <div
                [class.pointer-events-none]="f.readonly"
                [class.opacity-50]="f.readonly"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  [formControlName]="f.key"
                  [id]="f.key"
                />
              </div>
            </div>
            <label class="form-label mb-0" [for]="f.key">{{ f.label }}</label>
          </div>
        }

        @case ("NUMBER") {
          <label [for]="f.key" class="form-label"
            >{{ f.label }}
            @if (f?.validators?.required) {
              <span class="text-red-500">*</span>
            }
            @if (f.calculated) {
              <span class="badge-calc">Calculated</span>
            }
          </label>
          <input
            type="text"
            autocomplete="off"
            class="form-control"
            min="0"
            [attr.placeholder]="f.placeholder"
            [readonly]="f.readonly || f.calculated"
            [formControlName]="f.key"
            [id]="f.key"
            appAmountCommaFormatter
            [amountType]="f.amountType"
            (keydown)="onNumberKeyDown($event, f)"
          />
        }

        @case ("AMOUNT") {
          <div class="flex justify-between mb-1">
            <label class="form-label m-0"
              >{{ f.label }}
              @if (f?.validators?.required) {
                <span class="text-red-500">*</span>
              }
            </label>
            <div class="flex items-center gap-5">
              @for (
                o of [
                  { label: "%", value: "PERCENTAGE" },
                  { label: "Flat", value: "FLAT" },
                ];
                track o
              ) {
                <div class="inline-flex items-center gap-1">
                  <input
                    class="accent-(--color-primary)"
                    type="radio"
                    [attr.name]="f.key + '_type'"
                    [value]="o.value"
                    [formControlName]="f.typeKey"
                    [id]="f.key + '_' + o.value"
                  />
                  <label
                    class="form-label mb-0 cursor-pointer"
                    [for]="f.key + '_' + o.value"
                    >{{ o.label }}</label
                  >
                </div>
              }
            </div>
          </div>
          <input
            type="text"
            min="0"
            class="form-control"
            [attr.placeholder]="f.placeholder || 'Enter amount'"
            [readonly]="f.readonly || f.calculated"
            [formControlName]="f.key"
            [id]="f.key"
            appAmountCommaFormatter
            [amountType]="form.get(f.typeKey)?.value"
          />
        }

        @case ("DROPDOWN") {
          <label [for]="f.key" class="form-label"
            >{{ f.label }}
            @if (f?.validators?.required) {
              <span class="text-red-500">*</span>
            }
          </label>
          <ng-select
            [id]="f.key"
            [items]="f.options"
            bindLabel="value"
            bindValue="key"
            [formControlName]="f.key"
            [multiple]="f.multiple || false"
            [readonly]="f.readonly || f.calculated || f.disabled"
            notFoundText="No Data Found"
            class="form-selection"
            appendTo="body"
            [placeholder]="'Select ' + f.label"
            (change)="fieldChanged(f.key, $event)"
          >
          </ng-select>
        }

        @case ("CALENDAR") {
          <app-common-calendar-ad-bs
            [attr.placeholder]="f.placeholder"
            [label]="f.label"
            [key]="f.key"
            [shouldFloat]="false"
            [enableTime]="$any(f).enableTime"
            [formControlName]="f.key"
          >
          </app-common-calendar-ad-bs>
        }

        @case ("RADIO") {
          <label class="form-label">{{ f.label }}</label>
          <div class="flex flex-row flex-wrap justify-around gap-4">
            @for (o of f.options || []; track o) {
              <div class="flex items-center gap-1">
                <input
                  class="accent-(--color-primary)"
                  type="radio"
                  [value]="o.key"
                  [formControlName]="f.key"
                  [id]="f.key + '_' + o.key"
                />
                <label
                  class="form-label mb-0 cursor-pointer"
                  [attr.for]="f.key + '_' + o.key"
                  >{{ o.value }}</label
                >
              </div>
            }
          </div>
        }

        @case ("CHECKBOX") {
          <div class="flex items-center gap-2">
            <input
              class="accent-(--color-primary) m-0 shrink-0"
              type="checkbox"
              [formControlName]="f.key"
              [id]="f.key"
            />
            <label class="form-label m-0 cursor-pointer" [attr.for]="f.key">{{
              f.label || f.placeholder
            }}</label>
            @if (f.labelHtml) {
              <label
                [innerHTML]="sanitize(f.labelHtml)"
                class="form-label m-0"
              ></label>
            }
          </div>
        }

        @case ("TEXTAREA") {
          <label [for]="f.key" class="form-label">{{ f.label }}</label>
          <textarea
            class="form-control"
            rows="4"
            [readOnly]="f.readonly"
            [attr.placeholder]="f.placeholder"
            [formControlName]="f.key"
            [id]="f.key"
          >
          </textarea>
        }

        @case ("RICHTEXTEDITOR") {
          <label [for]="f.key" class="form-label">{{ f.label }}</label>
          <app-rich-editor
            [labelName]="f.labelName"
            [isSubmitted]="submitted"
            [id]="f.key"
            [formControlName]="f.key"
            [isReadOnly]="f.readonly"
          >
          </app-rich-editor>
        }

        @case ("NEPALI_INPUT") {
          <app-nepali-input
            [formControlName]="f.key"
            [label]="f.label"
            [placeholder]="f.placeholder"
            [inputId]="f.key"
            [readonly]="f.readonly"
            [required]="f?.validators?.required"
            [calculated]="!!f.calculated"
          >
          </app-nepali-input>
        }

        @case ("TIME") {
          <label [for]="f.key" class="form-label"
            >{{ f.label }}
            @if (f?.validators?.required) {
              <span class="text-red-500">*</span>
            }
            @if (f.calculated) {
              <span class="badge-calc">Calculated</span>
            }
          </label>
          <input
            #timeInput
            type="time"
            (click)="timeInput.showPicker()"
            class="form-control"
            [formControlName]="f.key"
            [id]="f.key"
            [attr.placeholder]="f.placeholder"
            [readonly]="f.readonly || f.calculated"
          />
        }

        @case ("FILE") {
          <div class="file-field-wrapper">
            <label [for]="f.key" class="form-label"
              >{{ f.label }}
              @if (f?.validators?.required) {
                <span class="text-red-500">*</span>
              }
            </label>
            <div class="custom-file-box">
              <label class="custom-file-btn" [for]="f.key">Choose file</label>
              <span class="custom-file-name">
                {{
                  uploadedFiles[f.key]?.file?.name ||
                    f.currentFileName ||
                    "No file chosen"
                }}
              </span>
              <input
                type="file"
                class="hidden"
                [id]="f.key"
                [attr.accept]="f.accept || '*/*'"
                (change)="onFileSelected($event, f.key)"
              />
            </div>
          </div>
        }

        @default {
          <label [for]="f.key" class="form-label">{{ f.label }}</label>
          <input
            type="text"
            class="form-control"
            [attr.placeholder]="f.placeholder"
            [formControlName]="f.key"
            [id]="f.key"
          />
        }
      }

      @if (isInvalid(f.key) && !(f?.readonly != null && f?.readonly === true)) {
        <span class="text-red-500 text-xs mt-0.5">{{
          getErrorMessage(f.key, f.errorMessages)
        }}</span>
      }
      @if (f.note) {
        <span class="text-yellow-600 text-xs mt-0.5">{{ f.note }}</span>
      }
    </div>
  </ng-container>
</ng-template>
